import clsx from 'clsx'
import React, { CSSProperties, useCallback } from 'react'

import { PagePresentation, PageType } from '@Generated/graphql'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { waitForElement } from '@Globals/DOM'
import { ImagePin as ImagePinData } from '@Globals/Image'
import { stringToPageType } from '@Globals/Page/Mappers'

import styles from './Image.module.scss'

export const ImagePin = ({
  className,
  id,
  scrollInToView,
  type,
  x,
  y,
}: ImagePinData & { className?: string }) => {
  const openModal = useCMSModalOpen()

  const onClick = useCallback(() => {
    if (scrollInToView) {
      // no reason to open the modal if the element already exists
      const element = document.querySelector(scrollInToView)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
    }

    openModal(stringToPageType(type) as PageType, id, undefined, PagePresentation.Sidebar)

    if (scrollInToView) {
      waitForElement(scrollInToView).then((element) => {
        if (!element) {
          return
        }
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }, [id, scrollInToView, type, openModal])

  return (
    <div
      aria-label="Pin"
      className={clsx(className, styles.pin)}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') {
          return
        }

        e.preventDefault()
        e.stopPropagation()
        onClick()
      }}
      role="button"
      style={{ '--x': x, '--y': y } as CSSProperties}
      tabIndex={0}
    />
  )
}

export const ImagePins = ({
  desktopPins,
  mobilePins,
  pins,
}: {
  desktopPins?: ImagePinData[] | null
  mobilePins?: ImagePinData[] | null
  pins?: ImagePinData[] | null
}) => {
  if (
    (!desktopPins || desktopPins.length === 0)
    && (!mobilePins || mobilePins.length === 0)
    && (!pins || pins.length === 0)
  ) {
    return null
  }

  return (
    <div className={styles.pins}>
      {desktopPins?.map(pin => <ImagePin key={`desktop-pin-${pin.x}-${pin.y}`} className={styles.desktop} {...pin} />)}
      {mobilePins?.map(pin => <ImagePin key={`mobile-pin-${pin.x}-${pin.y}`} className={styles.mobile} {...pin} />)}
      {pins?.map(pin => <ImagePin key={`pin-${pin.x}-${pin.y}`} {...pin} />)}
    </div>
  )
}
